import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import {
  getListAction,
  ManagerActionType,
} from '@src/admin/reducers/manager/managerActions'
import { StoreState } from '@admin/reducers'
import { Button } from '@mui/material'
import { useGlobalSnackbar, useLoading, usePagination } from '@src/hooks'
import { adminPath, toAdminRoute } from '@common/appConfig'
import ContentHeader from '@components/ContentHeader'
import Pagination from '@components/Pagination'
import ListTable from '@components/ListTable'
import { StatusCapsule } from '@components/Capsule'
import Snackbar, { SnackbarState } from '@src/components/Snackbar'
import commonMessages from '@common/messages'
import messages from '@admin/utils/messages'
import { ReactComponent as IconAdd } from '@src/assets/icons/iconAdd.svg'
import { BREADCRUMBS } from './utils'
import { AdminKeys } from '@admin/models/admin'
import { ManagerKeys } from '@src/admin/models/manager'
import { ErrorResponse } from '@src/utils/type'

export default function ManagerList() {
  const dispatch = useDispatch()
  const { managerList, managerTotal } = useSelector(
    (state: StoreState) => state.manager
  )
  const { formatMessage: f } = useIntl()
  const navigate = useNavigate()
  const {
    pagination: { count, page },
    handlePageChange,
    query,
  } = usePagination({
    count: managerTotal,
  })
  const { status, clearSnackbar, setSnackbar } = useGlobalSnackbar({
    page: 'manager',
  })
  const { openSpinner } = useLoading({
    endActions: {
      success: [ManagerActionType.GET_LIST_SUCCESS],
      error: [ManagerActionType.GET_LIST_ERROR],
    },
    cbk: {
      error: ({ error }: { error: ErrorResponse }) => {
        setSnackbar({
          state: SnackbarState.Error,
          message: error.message,
          notKeepData: true,
        })
        document.documentElement.scrollTop = 0
      },
    },
  })

  const theadList = [
    {
      id: ManagerKeys.Name,
      label: f(commonMessages.name),
    },
    {
      id: ManagerKeys.Email,
      label: f(commonMessages.email),
    },
    {
      id: ManagerKeys.Active,
      label: f(commonMessages.status),
      formatter: (isActivated: boolean) => (
        <StatusCapsule isActivated={isActivated} />
      ),
    },
  ]

  const handleRowClick = (id: string) =>
    navigate(generatePath(toAdminRoute(adminPath.manager.detail), { id }))

  useEffect(() => {
    openSpinner()
    dispatch(getListAction({ ...query }))
  }, [dispatch, query, openSpinner])

  useEffect(
    () => () => {
      clearSnackbar()
    },
    [dispatch, clearSnackbar]
  )

  return (
    <>
      {status && (
        <Snackbar
          severity={status.state}
          message={status.message!}
          onClose={clearSnackbar}
        />
      )}
      <ContentHeader
        title={f(messages.managerList)}
        breadcrumbs={BREADCRUMBS}
        buttonList={[
          <Button
            variant="contained"
            startIcon={<IconAdd />}
            onClick={() => navigate(toAdminRoute(adminPath.manager.create))}>
            {f(commonMessages.create)}
          </Button>,
        ]}
      />
      <ListTable
        theadList={theadList}
        dataList={managerList}
        rowIdKey={AdminKeys.Id}
        onRowClick={handleRowClick}
      />
      <Pagination count={count} page={page} onChange={handlePageChange} />
    </>
  )
}
